<script setup lang="ts">
import { useTranslation } from "i18next-vue";

defineProps<{
  icon: string;
  label: string;
  description: string;
  status?: "idle" | "completed" | "outdated";
  disabled?: boolean;
  loading?: boolean;
  comingSoon?: boolean;
}>();

defineEmits<{
  (e: "click"): void;
}>();

const { t } = useTranslation();
</script>

<template>
  <button
    class="ai-menu-item"
    :class="{
      disabled: disabled || comingSoon,
      loading: loading,
      completed: status === 'completed',
      outdated: status === 'outdated',
    }"
    :disabled="(disabled || comingSoon) && !loading"
    @click="$emit('click')"
  >
    <!-- Skeleton overlay when loading -->
    <div v-if="loading" class="skeleton-overlay"></div>

    <div class="item-icon" :class="{ 'icon-loading': loading }">
      <div v-if="loading" class="spinner"></div>
      <i v-else :class="icon" />
    </div>

    <div class="item-content">
      <div class="item-header">
        <span class="item-label">{{ label }}</span>
        <span
          v-if="status === 'completed' && !loading"
          class="status-badge status-completed"
        >
          <i class="bi-check-circle-fill" />
        </span>
        <span
          v-else-if="status === 'outdated' && !loading"
          class="status-badge status-outdated"
        >
          <i class="bi-exclamation-triangle-fill" />
        </span>
        <span v-if="loading" class="status-badge status-loading">
          {{ t("report:ai.status.generating") }}
        </span>
      </div>
      <span class="item-description">{{ description }}</span>
      <span v-if="comingSoon" class="coming-soon">
        <i class="bi-lock" />
        {{ t("report:ai.modules.comingSoon") }}
      </span>
    </div>

    <div v-if="!comingSoon" class="item-arrow">
      <i v-if="loading" class="bi-eye" />
      <i v-else class="bi-chevron-right" />
    </div>
  </button>
</template>

<style scoped lang="scss">
.ai-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 8px;
  position: relative;
  overflow: hidden;

  &:hover:not(.disabled) {
    background-color: var(--el-fill-color-light);
  }

  &:active:not(.disabled) {
    background-color: var(--el-fill-color);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.loading {
    cursor: pointer;
    background-color: rgba(139, 92, 246, 0.08);

    &:hover {
      background-color: rgba(139, 92, 246, 0.12);
    }
  }
}

// Skeleton shimmer overlay
.skeleton-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(139, 92, 246, 0.08) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  pointer-events: none;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.15) 0%,
    rgba(99, 102, 241, 0.15) 100%
  );
  color: #8b5cf6;
  font-size: 1.25rem;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  .disabled & {
    background: var(--el-fill-color);
    color: var(--el-text-color-secondary);
  }

  &.icon-loading {
    background: linear-gradient(
      135deg,
      rgba(139, 92, 246, 0.25) 0%,
      rgba(99, 102, 241, 0.25) 100%
    );
  }
}

// Spinner replacing icon when loading
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(139, 92, 246, 0.3);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  z-index: 1;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-label {
  font-weight: 500;
  font-size: 14px;
  color: var(--el-text-color-primary);

  .disabled & {
    color: var(--el-text-color-secondary);
  }

  .loading & {
    color: #8b5cf6;
  }
}

.item-description {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.3;

  .disabled & {
    color: var(--el-text-color-placeholder);
  }
}

.coming-soon {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--el-text-color-placeholder);
  margin-top: 2px;
}

.status-badge {
  display: flex;
  align-items: center;
  font-size: 14px;

  &.status-completed {
    color: #22c55e;
  }

  &.status-outdated {
    color: #f59e0b;
  }

  &.status-loading {
    font-size: 11px;
    font-weight: 500;
    color: #8b5cf6;
    background: rgba(139, 92, 246, 0.15);
    padding: 2px 8px;
    border-radius: 10px;
  }
}

.item-arrow {
  color: var(--el-text-color-placeholder);
  font-size: 14px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  .loading & {
    color: #8b5cf6;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
